import { AppleIcon, GoogleIcon, OutlookIcon } from "@assets";
import { CardBody, Flex } from "@chakra-ui/react";
import { NFTEventInfo } from "@models/event";
import { DEFAULT_LANGUAGE, Language } from "@models/language";
import { Timezone } from "@models/timezone";
import { saveAs } from "file-saver";
import { createEvent, DateArray, EventAttributes } from "ics";
import { DateTime, Zone } from "luxon";
import Image from "next/image";
import { HiCalendar } from "react-icons/hi";
import {
  CardCover,
  CustomCard,
  ShowChildrenOnHoverCard,
} from "../CheckoutSuccessCTA";

const _t = (key?: string, language = DEFAULT_LANGUAGE) => key || "";

interface CalendarCardInfo {
  apple?: string;
  outlook?: string;
  gmail?: string;
}

const CalendarCard = ({
  calendarCardInfo: { apple, outlook, gmail } = {},
  info,
  language = DEFAULT_LANGUAGE,
  start = 0,
  end = 9999999999999,
  timezone = Timezone["Asia/Hong_Kong"],
  isHover = false,
}: {
  calendarCardInfo?: CalendarCardInfo;
  info?: NFTEventInfo;
  language?: Language;
  start?: number;
  end?: number;
  timezone?: Timezone;
  isHover?: boolean;
}) => {
  const toDateArray = (
    timestamp: number,
    timezone: string | Zone = "Asia/Hong_Kong"
  ): DateArray => {
    const date = DateTime.fromMillis(timestamp, { zone: timezone });
    return [date.year, date.month, date.day, date.hour, date.minute];
  };

  const toLuxonDurationObject = (
    start: number,
    end: number,
    timezone: string | Zone = "Asia/Hong_Kong"
  ) => {
    const startDate = DateTime.fromMillis(start, { zone: timezone });
    const endDate = DateTime.fromMillis(end, { zone: timezone });
    const duration =
      end == 9999999999999
        ? { days: 365, hours: 0, minutes: 0, seconds: 0 }
        : endDate
            .diff(startDate, ["days", "hours", "minutes", "seconds"])
            .toObject();
    return duration;
  };

  const event: EventAttributes = {
    start: toDateArray(start ?? 0, timezone),
    duration: toLuxonDurationObject(start, end, timezone),
    title: info?.name,
    location: info?.location,
    url: info?.locationUrl,
    status: "CONFIRMED",
  };

  const handleSave = () => {
    createEvent(event as EventAttributes, (error, value) => {
      const blob = new Blob([value], { type: "text/calendar;charset=utf-8" });
      saveAs(blob, `${info?.name || "event"}.ics`);
    });
  };

  if (isHover)
    return (
      <ShowChildrenOnHoverCard
        cover=<CardCover
          icon={<HiCalendar />}
          alt="add calendar icon"
          title={_t("Add to Calendar", language)}
        />
      >
        <Flex
          direction="row"
          gap={6}
          justifyContent="center"
          width="100%"
          alignItems="center"
        >
          <button
            className="w-[1.5rem] h-[1.5rem] flex items-center flex-shrink-0 svg-primary cursor-pointer"
            onClick={handleSave}
          >
            <Image src={AppleIcon} alt="apple mail icon" />
          </button>
          <button
            className="w-[1.5rem] h-[1.5rem] flex items-center flex-shrink-0 svg-primary cursor-pointer"
            onClick={handleSave}
          >
            <Image src={OutlookIcon} alt="outlook icon" />
          </button>
          <button
            className="w-[1.5rem] h-[1.5rem] flex items-center flex-shrink-0 svg-primary cursor-pointer"
            onClick={handleSave}
          >
            <Image src={GoogleIcon} alt="gmail icon" />
          </button>
        </Flex>
      </ShowChildrenOnHoverCard>
    );

  return (
    <CustomCard onClick={handleSave}>
      <CardBody>
        <CardCover
          icon={<HiCalendar />}
          alt="add calendar icon"
          title={_t("Add to Calendar", language)}
        />
      </CardBody>
    </CustomCard>
  );
};

export default CalendarCard;
